import React from "react";
import ClaimButton from "@components/FlowActionButtons/ClaimButton";
import { useNotification } from "@context/NotificationContext";
import { claimMovementById } from "@services/movementServicesApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { canUserClaim } from "./utils";

interface ClaimMovementProps {
  movementFlowData?: {
    id: string;
    task: { status: string; claimOwnerName: string } | null;
    status: string;
    terminalReport: { report: string } | null;
    to: string;
    from: string;
  };
}

const LANG_BASE = "vessel-call.movement-services";

const ClaimMovement: React.FC<ClaimMovementProps> = ({ movementFlowData }) => {
  const service = "bff-vessel-call";
  const { notify } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { vesselId, vesselCallId } = useOutletContext<{ vesselId: string; vesselCallId: string }>();

  const claimMovementMutation = useMutation({
    mutationFn: (id: string) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "MovementService",
          action: "claim_movement_service",
          entityType: "movement_service",
        },
        (transactionId) =>
          claimMovementById({
            service,
            id,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async () => {
      // Invalidate the precise grid key to preserve params during refetch
      queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });
      // Invalidate and refetch the preview
      queryClient.invalidateQueries({
        queryKey: ["movement-service-preview", movementFlowData?.id],
      });
      await queryClient.refetchQueries({
        queryKey: ["movement-service-preview", movementFlowData?.id],
      });
      notify({
        title: t("vessel-call.movement-services.movement-claimed"),
        type: "success",
      });
    },
    onError: (error: Error) =>
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      }),
  });

  if (!movementFlowData) {
    return null;
  }

  if (!movementFlowData.task) {
    return null;
  }

  const isActionAvailable = canUserClaim(movementFlowData.task.status, movementFlowData.status);

  const handleClaimClick = () => {
    claimMovementMutation.mutate(movementFlowData.id);
  };
  return (
    <ClaimButton
      langBase={LANG_BASE}
      permission="canClaimMovementService"
      isActionAvailable={isActionAvailable}
      onClaim={handleClaimClick}
    />
  );
};

export default ClaimMovement;
